/**
 * Suma de verificación de los respaldos (Req. 6.1, 6.7, 6.13).
 * Origen: Control de Negocio de Comida, sin cambios de lógica.
 *
 * Permite detectar un archivo corrupto, truncado o editado a mano ANTES de
 * reemplazar los datos del dispositivo.
 *
 * Usa SHA-256 de `crypto.subtle`, disponible de forma nativa en el navegador
 * y en Node, sin agregar dependencias.
 */

/**
 * Serializa de forma DETERMINISTA: las claves de cada objeto se ordenan
 * alfabeticamente.
 *
 * Sin este orden, dos serializaciones del mismo contenido podrian producir
 * cadenas distintas y, por lo tanto, sumas distintas, lo que haria que un
 * respaldo valido pareciera corrupto.
 */
export function serializarDeterminista(valor: unknown): string {
  if (valor === null || typeof valor !== 'object') {
    return JSON.stringify(valor) ?? 'null';
  }

  if (Array.isArray(valor)) {
    return `[${valor.map(serializarDeterminista).join(',')}]`;
  }

  const registro = valor as Record<string, unknown>;
  const partes = Object.keys(registro)
    .sort()
    .filter((clave) => registro[clave] !== undefined)
    .map((clave) => `${JSON.stringify(clave)}:${serializarDeterminista(registro[clave])}`);

  return `{${partes.join(',')}}`;
}

function aHexadecimal(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * SHA-256 en hexadecimal de unos bytes TAL CUAL, sin serializar nada. Es la
 * huella de cada foto de progreso: viaja en el JSON del respaldo y al
 * restaurar se vuelve a calcular sobre el archivo del ZIP, así que una foto
 * dañada no entra (design.md 10.7).
 */
export async function sha256DeBytes(bytes: BufferSource): Promise<string> {
  return aHexadecimal(await globalThis.crypto.subtle.digest('SHA-256', bytes));
}

/** Calcula la suma de verificacion. Devuelve `"sha256:<hexadecimal>"`. */
export async function calcularChecksum(valor: unknown): Promise<string> {
  const texto = serializarDeterminista(valor);
  const bytes = new TextEncoder().encode(texto);
  const digestion = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return `sha256:${aHexadecimal(digestion)}`;
}

/** Compara en tiempo constante para no filtrar informacion por el tiempo. */
export function checksumCoincide(esperado: string, calculado: string): boolean {
  if (esperado.length !== calculado.length) return false;
  let diferencia = 0;
  for (let i = 0; i < esperado.length; i++) {
    diferencia |= esperado.charCodeAt(i) ^ calculado.charCodeAt(i);
  }
  return diferencia === 0;
}
